import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { professionalsAPI, reviewsAPI, bookingsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatDate, formatPrice, getCategoryImage } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { MapPin, Star, Clock, Euro, Calendar as CalendarIcon, Phone, Mail, ArrowLeft, CheckCircle } from 'lucide-react';

const ProfessionalDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, isClient } = useAuth();
  const [professional, setProfessional] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingData, setBookingData] = useState({
    scheduled_date: null,
    description: '',
    address: '',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [proRes, reviewsRes] = await Promise.all([
          professionalsAPI.getOne(id),
          reviewsAPI.getByProfessional(id),
        ]);
        setProfessional(proRes.data);
        setReviews(reviewsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Professionnel non trouvé');
        navigate('/search');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, navigate]);

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    
    if (!bookingData.scheduled_date) {
      toast.error('Veuillez sélectionner une date');
      return;
    }

    setBookingLoading(true);
    try {
      await bookingsAPI.create({
        professional_id: id,
        scheduled_date: bookingData.scheduled_date.toISOString(),
        description: bookingData.description,
        address: bookingData.address,
      });
      toast.success('Réservation envoyée avec succès !');
      setBookingOpen(false);
      setBookingData({ scheduled_date: null, description: '', address: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la réservation');
    } finally {
      setBookingLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="h-64 rounded-xl skeleton-loading mb-8"></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="md:col-span-2 h-96 rounded-xl skeleton-loading"></div>
          <div className="h-64 rounded-xl skeleton-loading"></div>
        </div>
      </div>
    );
  }

  if (!professional) return null;

  return (
    <div className="min-h-screen pb-12" data-testid="professional-detail-page">
      {/* Hero Section */}
      <div className="relative h-64 md:h-80">
        <img
          src={getCategoryImage(professional.category_key)}
          alt={professional.trade}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/40 to-transparent"></div>
        <button
          onClick={() => navigate(-1)}
          className="absolute top-6 left-6 flex items-center gap-2 text-white bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full hover:bg-white/30 transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="h-4 w-4" />
          Retour
        </button>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Profile Card - Overlapping */}
        <div className="-mt-24 relative z-10 mb-8">
          <Card className="border-slate-100 shadow-lg">
            <CardContent className="p-6 md:p-8">
              <div className="flex flex-col md:flex-row gap-6 items-start">
                {/* Avatar */}
                <div className="w-24 h-24 rounded-xl bg-slate-900 flex items-center justify-center text-3xl text-white font-semibold shrink-0">
                  {professional.user_name.split(' ').map(n => n[0]).join('')}
                </div>
                
                {/* Info */}
                <div className="flex-1">
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                    <div>
                      <h1 className="text-2xl md:text-3xl text-slate-900 mb-1" style={{ fontFamily: '"DM Serif Display", serif' }}>
                        {professional.user_name}
                      </h1>
                      <p className="text-lg text-orange-600 font-medium mb-2">
                        {professional.trade}
                      </p>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600">
                        <span className="flex items-center gap-1">
                          <MapPin className="h-4 w-4" />
                          {professional.city}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {professional.experience_years} ans d'expérience
                        </span>
                        <span className="flex items-center gap-1">
                          <Star className="h-4 w-4 text-orange-500 fill-orange-500" />
                          {professional.average_rating > 0 ? professional.average_rating.toFixed(1) : 'Nouveau'}
                          {professional.total_reviews > 0 && ` (${professional.total_reviews} avis)`}
                        </span>
                      </div>
                    </div>
                    
                    {/* Price & Booking */}
                    <div className="flex flex-col items-start md:items-end gap-3">
                      <div className="text-right">
                        <p className="text-3xl font-bold text-slate-900">
                          {formatPrice(professional.hourly_rate)}
                        </p>
                        <p className="text-sm text-slate-500">par heure</p>
                      </div>
                      
                      {isClient ? (
                        <Dialog open={bookingOpen} onOpenChange={setBookingOpen}>
                          <DialogTrigger asChild>
                            <Button
                              className="bg-orange-600 hover:bg-orange-700 text-white rounded-full px-8 h-12 btn-press shadow-lg shadow-orange-600/20"
                              disabled={!professional.available}
                              data-testid="book-btn"
                            >
                              {professional.available ? 'Réserver' : 'Indisponible'}
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="sm:max-w-md">
                            <DialogHeader>
                              <DialogTitle style={{ fontFamily: '"DM Serif Display", serif' }}>
                                Réserver {professional.user_name}
                              </DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleBookingSubmit} className="space-y-4 pt-4">
                              <div className="space-y-2">
                                <Label>Date souhaitée</Label>
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <Button
                                      variant="outline"
                                      className="w-full justify-start text-left font-normal h-12 rounded-lg"
                                      data-testid="booking-date-btn"
                                    >
                                      <CalendarIcon className="mr-2 h-4 w-4" />
                                      {bookingData.scheduled_date
                                        ? format(bookingData.scheduled_date, 'PPP', { locale: fr })
                                        : 'Sélectionner une date'}
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-auto p-0" align="start">
                                    <Calendar
                                      mode="single"
                                      selected={bookingData.scheduled_date}
                                      onSelect={(date) => setBookingData({ ...bookingData, scheduled_date: date })}
                                      disabled={(date) => date < new Date()}
                                      initialFocus
                                    />
                                  </PopoverContent>
                                </Popover>
                              </div>
                              
                              <div className="space-y-2">
                                <Label htmlFor="address">Adresse d'intervention</Label>
                                <Input
                                  id="address"
                                  placeholder="123 rue de la Paix, 75001 Paris"
                                  value={bookingData.address}
                                  onChange={(e) => setBookingData({ ...bookingData, address: e.target.value })}
                                  className="h-12 rounded-lg"
                                  required
                                  data-testid="booking-address-input"
                                />
                              </div>
                              
                              <div className="space-y-2">
                                <Label htmlFor="description">Description de votre besoin</Label>
                                <Textarea
                                  id="description"
                                  placeholder="Décrivez votre projet ou problème..."
                                  value={bookingData.description}
                                  onChange={(e) => setBookingData({ ...bookingData, description: e.target.value })}
                                  rows={4}
                                  className="rounded-lg resize-none"
                                  required
                                  data-testid="booking-description-input"
                                />
                              </div>
                              
                              <Button
                                type="submit"
                                className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white rounded-full btn-press"
                                disabled={bookingLoading}
                                data-testid="booking-submit-btn"
                              >
                                {bookingLoading ? 'Envoi...' : 'Confirmer la réservation'}
                              </Button>
                            </form>
                          </DialogContent>
                        </Dialog>
                      ) : !isAuthenticated ? (
                        <Button
                          onClick={() => navigate('/login')}
                          className="bg-orange-600 hover:bg-orange-700 text-white rounded-full px-8 h-12 btn-press"
                          data-testid="login-to-book-btn"
                        >
                          Connectez-vous pour réserver
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="md:col-span-2 space-y-8">
            {/* Description */}
            <Card className="border-slate-100">
              <CardHeader>
                <CardTitle style={{ fontFamily: '"DM Serif Display", serif' }}>
                  À propos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 leading-relaxed">
                  {professional.description}
                </p>
              </CardContent>
            </Card>

            {/* Reviews */}
            <Card className="border-slate-100">
              <CardHeader>
                <CardTitle style={{ fontFamily: '"DM Serif Display", serif' }}>
                  Avis clients ({reviews.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {reviews.length === 0 ? (
                  <div className="text-center py-8">
                    <Star className="h-12 w-12 mx-auto text-slate-200 mb-4" />
                    <p className="text-slate-500">Pas encore d'avis</p>
                    <p className="text-sm text-slate-400">Soyez le premier à donner votre avis !</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {reviews.map((review) => (
                      <div key={review.id} className="border-b border-slate-100 last:border-0 pb-6 last:pb-0">
                        <div className="flex items-center justify-between mb-2">
                          <p className="font-medium text-slate-900">{review.client_name}</p>
                          <div className="flex items-center gap-1">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`h-4 w-4 ${
                                  i < review.rating
                                    ? 'text-orange-500 fill-orange-500'
                                    : 'text-slate-200'
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                        <p className="text-slate-600 text-sm mb-1">{review.comment}</p>
                        <p className="text-xs text-slate-400">{formatDate(review.created_at)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Card */}
            <Card className="border-slate-100">
              <CardHeader>
                <CardTitle style={{ fontFamily: '"DM Serif Display", serif' }}>
                  Contact
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {professional.user_phone && (
                  <a
                    href={`tel:${professional.user_phone}`}
                    className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                    data-testid="phone-link"
                  >
                    <Phone className="h-5 w-5 text-slate-600" />
                    <span className="text-slate-900">{professional.user_phone}</span>
                  </a>
                )}
                <a
                  href={`mailto:${professional.user_email}`}
                  className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                  data-testid="email-link"
                >
                  <Mail className="h-5 w-5 text-slate-600" />
                  <span className="text-slate-900 truncate">{professional.user_email}</span>
                </a>
              </CardContent>
            </Card>

            {/* Info Card */}
            <Card className="border-slate-100">
              <CardHeader>
                <CardTitle style={{ fontFamily: '"DM Serif Display", serif' }}>
                  Informations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Catégorie</span>
                    <span className="text-slate-900 font-medium">{professional.category_name.replace('Métiers ', '')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Expérience</span>
                    <span className="text-slate-900 font-medium">{professional.experience_years} ans</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Statut</span>
                    <span className={`font-medium flex items-center gap-1 ${professional.available ? 'text-green-600' : 'text-red-600'}`}>
                      <CheckCircle className="h-4 w-4" />
                      {professional.available ? 'Disponible' : 'Indisponible'}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfessionalDetailPage;
